package kz.dentalcrm.app.screens.treatment.add

import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.async
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.utils.Result
import kz.dentalcrm.app.core.utils.stateHandle
import kz.dentalcrm.app.data.network.request_model.StoreTreatmentRequest
import kz.dentalcrm.app.data.network.request_model.TreatmentDataItem
import kz.dentalcrm.app.data.network.response_model.diagnosis.Diagnosis
import kz.dentalcrm.app.data.network.response_model.protocol_templates.TemplateItem
import kz.dentalcrm.app.domain.DiagnosisRepository
import kz.dentalcrm.app.domain.TreatmentRepository
import kz.dentalcrm.app.screens.treatment.add.mvi.AddPatientTreatmentEvent
import kz.dentalcrm.app.screens.treatment.add.mvi.AddPatientTreatmentSideEffect
import kz.dentalcrm.app.screens.treatment.add.mvi.AddPatientTreatmentState
import org.koin.core.component.inject

class AddPatientTreatmentViewModel : BaseStateViewModel<
        AddPatientTreatmentEvent,
        AddPatientTreatmentState,
        AddPatientTreatmentSideEffect>() {

    private val treatmentRepository: TreatmentRepository by inject()
    private val diagnosisRepository: DiagnosisRepository by inject()

    override fun setInitialState(): AddPatientTreatmentState = AddPatientTreatmentState()

    override fun handleEvents(event: AddPatientTreatmentEvent) {
        when (event) {
            is AddPatientTreatmentEvent.LoadData -> loadData(event.appointmentId, event.initialTeeth)
            is AddPatientTreatmentEvent.ToggleTooth -> toggleTooth(event.toothId)
            is AddPatientTreatmentEvent.SelectTemplate -> selectTemplate(event.template)
            is AddPatientTreatmentEvent.SelectDiagnosis -> selectDiagnosis(event.diagnosis)
            is AddPatientTreatmentEvent.SelectOption -> selectOption(event.typeId, event.optionId)
            is AddPatientTreatmentEvent.UpdateCustomValue -> updateCustomValue(event.typeId, event.value)
            is AddPatientTreatmentEvent.Save -> save()
            is AddPatientTreatmentEvent.OnBack -> setEffect { AddPatientTreatmentSideEffect.NavigateBack }
        }
    }

    private fun loadData(appointmentId: Int, initialTeeth: List<String>) {
        setState {
            copy(
                isLoading = true,
                appointmentId = appointmentId,
                selectedTeeth = initialTeeth,
                // Reset selection state for fresh form
                selectedDiagnosis = null,
                selectedTemplate = null,
                selectedOptions = emptyMap(),
                customValues = emptyMap(),
                templateDetails = emptyList<kz.dentalcrm.app.data.network.response_model.protocol_templates.TreatmentDetailResponseItem>().toPersistentList(),
                enableSave = false
            )
        }

        launch {
            val templatesDeferred = async { treatmentRepository.getTemplates() }
            val diagnosesDeferred = async { diagnosisRepository.getDiagnosis(search = "", page = 1, pageSize = 100) }

            val templatesResult = templatesDeferred.await()
            val diagnosesResult = diagnosesDeferred.await()

            templatesResult.stateHandle { templates ->
                setState {
                    copy(templates = templates?.toPersistentList() ?: templates.orEmpty().toPersistentList())
                }
            }

            diagnosesResult.stateHandle { diagnosisResponse ->
                setState {
                    copy(diagnoses = diagnosisResponse?.data?.toPersistentList() ?: emptyList<Diagnosis>().toPersistentList())
                }
            }

            setState { copy(isLoading = false) }
            checkButtonState()
        }
    }

    private fun toggleTooth(toothId: String) {
        val currentTeeth = viewState.value.selectedTeeth
        val newTeeth = if (currentTeeth.contains(toothId)) {
            currentTeeth - toothId
        } else {
            currentTeeth + toothId
        }
        setState { copy(selectedTeeth = newTeeth) }
        checkButtonState()
    }

    private fun selectTemplate(template: TemplateItem) {
        setState {
            copy(
                selectedTemplate = template,
                templateDetails = emptyList<kz.dentalcrm.app.data.network.response_model.protocol_templates.TreatmentDetailResponseItem>().toPersistentList(),
                selectedOptions = emptyMap()
            )
        }
        loadTemplateDetails(template.id.toString())
        checkButtonState()
    }

    private fun loadTemplateDetails(templateId: String) {
        launch {
            treatmentRepository.getTreatmentDetail(templateId).stateHandle { details ->
                setState {
                    copy(templateDetails = details?.toPersistentList() ?: emptyList<kz.dentalcrm.app.data.network.response_model.protocol_templates.TreatmentDetailResponseItem>().toPersistentList())
                }
            }
        }
    }

    private fun selectDiagnosis(diagnosis: Diagnosis) {
        setState { copy(selectedDiagnosis = diagnosis) }
        checkButtonState()
    }

    private fun selectOption(typeId: Int, optionId: Int) {
        val currentOptions = viewState.value.selectedOptions.toMutableMap()
        val currentList = currentOptions[typeId]?.toMutableList() ?: mutableListOf()
        
        if (currentList.contains(optionId)) {
            currentList.remove(optionId)
        } else {
            currentList.add(optionId)
        }
        
        if (currentList.isEmpty()) {
            currentOptions.remove(typeId)
        } else {
            currentOptions[typeId] = currentList
        }
        
        setState { copy(selectedOptions = currentOptions) }
    }

    private fun updateCustomValue(typeId: Int, value: String) {
        val currentValues = viewState.value.customValues.toMutableMap()
        currentValues[typeId] = value
        setState { copy(customValues = currentValues) }
    }

    private fun checkButtonState() {
        val currentState = viewState.value
        setState {
            copy(enableSave = currentState.selectedDiagnosis != null)
        }
    }

    private fun save() {
        val currentState = viewState.value
        if (currentState.isSaving || !currentState.enableSave) return

        val template = currentState.selectedTemplate
        val diagnosis = currentState.selectedDiagnosis ?: return

        setState { copy(isSaving = true) }

        launch {
            val dataItems = currentState.selectedOptions.flatMap { (typeId, optionIds) ->
                optionIds.map { optionId ->
                    TreatmentDataItem(
                        typeId = typeId,
                        optionId = optionId,
                        isChecked = true
                    )
                }
            }

            val request = StoreTreatmentRequest(
                templateId = template?.id?.toString(),
                appointmentId = currentState.appointmentId.toString(),
                toothNumber = 0,
                diagnosisId = diagnosis.id.toString(),
                isFinished = false,
                teeth = currentState.selectedTeeth,
                data = dataItems
            )

            when (val result = treatmentRepository.storeTreatment(request)) {
                is Result.Success -> {
                    setState { copy(isSaving = false) }
                    setEffect { AddPatientTreatmentSideEffect.NavigateBack }
                }
                is Result.Error -> {
                    setState { copy(isSaving = false) }
                    setEffect {
                        AddPatientTreatmentSideEffect.ShowError(
                            result.message ?: "Error saving treatment"
                        )
                    }
                }
                else -> {
                    setState { copy(isSaving = false) }
                    setEffect { AddPatientTreatmentSideEffect.ShowError("Unknown error") }
                }
            }
        }
    }
}
